import React, { useState } from 'react';
import { FiPlay, FiVideo } from 'react-icons/fi';
import ContentModal from './ContentModal';
import { fullUrl } from '../api';
import { useLanguage } from '../i18n';
import { VIDEO_LABEL, parseVideo } from '../utils/video';

/** Right sidebar: owner video links. Thumbnail auto-generated, hover = autoplay,
 *  click = full detail modal. Works for YouTube / Vimeo / TikTok / Facebook /
 *  Instagram links (others open as a normal link). */
export default function ShopSidebarRight({ videos = [] }) {
  const { t } = useLanguage();
  const [hover, setHover] = useState('');
  const [active, setActive] = useState(null);

  if (!videos.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-4 text-sm text-gray-400">
        <FiVideo className="w-5 h-5 mb-2" />
        {t('noVideos')}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center gap-2 text-sm font-bold">
          <FiVideo className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          {t('videos')}
        </div>
        <div className="divide-y dark:divide-gray-700">
          {videos.map((v) => {
            const info = parseVideo(v.url);
            // The owner can upload a custom thumbnail (1280x720); it wins over the auto one.
            const thumb = v.thumbnail ? fullUrl(v.thumbnail) : info.thumb;
            const playing = hover === v.id && !!info.embed;
            return (
              <div
                key={v.id}
                className="p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/60 transition-colors anim-fade-up"
                onMouseEnter={() => setHover(v.id)}
                onMouseLeave={() => setHover('')}
                onClick={() => setActive(v)}
              >
                <div className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900 aspect-video">
                  {playing ? (
                    <iframe
                      src={info.autoplay || info.embed}
                      title={v.title || 'video'}
                      className="w-full h-full pointer-events-none"
                      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                      allowFullScreen
                      frameBorder="0"
                    />
                  ) : thumb ? (
                    <>
                      <img src={thumb} alt="" loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                        <span className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                          <FiPlay className="w-4 h-4 ml-0.5" style={{ color: 'var(--primary)' }} />
                        </span>
                      </span>
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-xs text-white"
                      style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))' }}>
                      <FiPlay className="w-6 h-6" />
                      {VIDEO_LABEL[info.type] || 'Video'}
                    </div>
                  )}
                </div>
                <div className="mt-2 font-semibold text-sm leading-snug line-clamp-2">
                  {v.title || VIDEO_LABEL[info.type] || 'Video'}
                </div>
                {v.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{v.description}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {active && (() => {
        const info = parseVideo(active.url);
        return (
          <ContentModal title={active.title || VIDEO_LABEL[info.type] || 'Video'} link={active.url}
            onClose={() => setActive(null)}>
            {info.embed ? (
              <div className="aspect-video rounded-xl overflow-hidden bg-black">
                <iframe
                  src={info.autoplay || info.embed}
                  title={active.title || 'video'}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            ) : (
              <div className="aspect-video rounded-xl overflow-hidden flex flex-col items-center justify-center text-white gap-2"
                style={{ background: 'linear-gradient(135deg, var(--primary), var(--secondary))' }}>
                <FiVideo className="w-10 h-10" />
                <span className="text-sm">{VIDEO_LABEL[info.type] || 'Video'}</span>
              </div>
            )}
            {active.description && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{active.description}</p>
            )}
          </ContentModal>
        );
      })()}
    </>
  );
}
